import { useState, useEffect } from 'react';

interface UseTypingAnimationOptions {
  text: string;
  shouldAnimate?: boolean;
  speed?: number;
  onDoneAnimation?: () => void;
}

const useTypingAnimation = ({ text, shouldAnimate = true, speed = 10, onDoneAnimation }: UseTypingAnimationOptions) => {
  const [typedText, setTypedText] = useState('');
  const [isDone, setIsDone] = useState(false);
  const [bufferDots, setBufferDots] = useState('');

  useEffect(() => {
    if (!shouldAnimate) {
      setTypedText(text);
      setIsDone(true);
      return;
    }

    let intervalId: NodeJS.Timeout | null = null;

    if (text) {
      const typeText = () => {
        if (typedText.length < text.length) {
          setTypedText(text.substring(0, typedText.length + 1));
        } else if (intervalId) {
          setIsDone(true);
          clearInterval(intervalId);
        }
      };

      intervalId = setInterval(typeText, speed);

      return () => {
        if (!intervalId) return;

        clearInterval(intervalId);
      };
    }
  }, [text, typedText, speed, shouldAnimate]);

  useEffect(() => {
    if (!isDone) return;
    let dotIntervalId: NodeJS.Timeout | null = null;

    const updateBufferDots = () => {
      if (bufferDots.length < 3) {
        setBufferDots(bufferDots + '.');
      } else {
        setBufferDots('');
      }
    };

    dotIntervalId = setInterval(updateBufferDots, 100);

    return () => {
      if (!dotIntervalId) return;

      clearInterval(dotIntervalId);
    };
  }, [isDone, bufferDots]);

  useEffect(() => {
    if (!shouldAnimate || !isDone || !onDoneAnimation) return;

    onDoneAnimation();
  }, [isDone, shouldAnimate]);

  return { typedText: `${typedText}${bufferDots}`, isDone };
};

export default useTypingAnimation;